import React, { useState, useEffect } from 'react';
import Icon from '../../../../shared/components/Icon';
import FilterPopup from '../../../../shared/components/modals/FilterPopup';
import { useOverviewFilters } from '../../../../shared/context/OverviewFilterContext';
import SourceChart from './components/SourceChart';
import LocationChart from './components/LocationChart';
import SourceLocationMatrix from './components/SourceLocationMatrix';
import TrafficQualityChart from './components/TrafficQualityChart';
import CampaignSummaryTable from './components/CampaignSummaryTable';
import axiosInstance from '../../../../shared/services/api/axiosInstance';
import { getDefaultDateRange } from '../../../../shared/utils/dateUtils';

const Source = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { appliedFilters, updateFilters, hasActiveFilters, teamId } = useOverviewFilters();
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  // Initialize default filters (first website and first contract) when Source mounts
  useEffect(() => {
    const initializeDefaultFilters = async () => {
      if (!teamId || hasInitializedFilters) return;
      
      // Only initialize if no filters are currently set
      const hasWebsites = appliedFilters.websites && appliedFilters.websites.length > 0;
      const hasContracts = appliedFilters.contracts && appliedFilters.contracts.length > 0;
      
      if (hasWebsites || hasContracts) {
        setHasInitializedFilters(true);
        return;
      }

      try {
        // Fetch websites
        let firstWebsiteId = null;
        try {
          const isNumericId = typeof teamId === 'number' || (!isNaN(teamId) && Number.isInteger(Number(teamId)));
          const websitesResponse = isNumericId
            ? await axiosInstance.get(`/website/team/${teamId}`)
            : await axiosInstance.get(`/website/name/${encodeURIComponent(teamId)}`);
          
          if (websitesResponse.data?.websites?.length > 0) {
            const activeWebsites = websitesResponse.data.websites.filter(w => w.is_verified || w.isVerified);
            if (activeWebsites.length > 0) {
              firstWebsiteId = activeWebsites[0].site_id || activeWebsites[0].siteId;
            }
          }
        } catch (err) {
          console.warn('Error fetching websites for default filter:', err);
        }

        // Fetch contracts
        let firstContract = null;
        try {
          let teamName = teamId;
          if (typeof teamId === 'number' || !isNaN(teamId)) {
            teamName = localStorage.getItem('selectedTeam') || teamId;
          }
          
          const contractsResponse = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
          
          if (contractsResponse.data?.contracts?.length > 0) {
            const contract = contractsResponse.data.contracts[0];
            firstContract = {
              id: contract.contractId || contract.id,
              address: contract.address || contract.contract_address,
              name: contract.name || contract.contract_name || `Contract ${(contract.address || contract.contract_address || '').substring(0, 6)}...${(contract.address || contract.contract_address || '').substring((contract.address || contract.contract_address || '').length - 4)}`,
              blockchain: contract.blockchain || contract.chain,
              tokenSymbol: contract.tokenSymbol || contract.symbol || contract.contract_symbol,
              contractType: contract.contractType || 'main'
            };
          }
        } catch (err) {
          console.warn('Error fetching contracts for default filter:', err);
        }

        // Update filters with first website and first contract
        if (firstWebsiteId || firstContract) {
          updateFilters({
            websites: firstWebsiteId ? [firstWebsiteId] : [],
            contracts: firstContract ? [firstContract] : [],
            sessions: appliedFilters.sessions || [],
            userIdentities: appliedFilters.userIdentities || [],
            dateRange: appliedFilters.dateRange || getDefaultDateRange()
          });
        }
        setHasInitializedFilters(true);
      } catch (error) {
        console.error('Error initializing default filters:', error);
        setHasInitializedFilters(true); // Set to true even on error to prevent infinite loops
      }
    };

    initializeDefaultFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, hasInitializedFilters]);

  // Reset initialization flag when team changes
  useEffect(() => {
    setHasInitializedFilters(false);
  }, [teamId]);

  const handleFiltersChange = (filters) => {
    updateFilters(filters);
    // Here you would refetch data based on the new filters
    console.log('Source: Applied filters:', filters);
  };

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  return (
    <div className="relative h-full overflow-y-auto overflow-x-hidden" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* Filter Button - Vertical, fixed to right edge */}
      <button
        onClick={() => setIsFilterOpen(true)}
        className="fixed z-10 transition-colors shadow-sm flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-[#475569]"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          right: 0,
          top: '16px',
          padding: '16px 8px',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'none',
          border: '1px solid #DDE2EE',
          borderRight: 'none',
          borderRadius: 0
        }}
      >
        <Icon name="filter" size={18} style={{ transform: 'rotate(90deg)' }} />
        <span className="text-sm font-medium">Filters</span>
        {hasActiveFilters() && (
          <span className="text-xs opacity-90">•</span>
        )}
      </button>

      {/* Page Content */}
      <div 
        className="p-4 md:p-6 lg:p-8 pr-20" 
        style={{ 
          width: '100%', 
          maxWidth: '100%',
          overflow: 'hidden', 
          boxSizing: 'border-box',
          marginRight: 0
        }}
      >
        {/* Source and Location Charts - Side by Side */}
        <div 
          id="charts-container"
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4" 
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', minWidth: 0 }}
        >
          {/* Source Chart */}
          <div style={{ minWidth: 0, width: '100%' }}>
            <SourceChart 
              teamId={teamId}
              appliedFilters={appliedFilters}
              isLoading={false}
            />
          </div>
          
          {/* Location Chart */}
          <div style={{ minWidth: 0, width: '100%' }}>
            <LocationChart 
              teamId={teamId}
              appliedFilters={appliedFilters}
              isLoading={false}
            />
          </div>
        </div>

        {/* Source-Location Cross Matrix - Match width of charts above */}
        <div 
          className="mt-4" 
          style={{ 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden', 
            boxSizing: 'border-box', 
            minWidth: 0,
            display: 'flex'
          }}
        >
          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <SourceLocationMatrix 
              teamId={teamId}
              appliedFilters={appliedFilters}
              isLoading={false}
            />
          </div>
        </div>

        {/* Traffic Quality Analysis Chart - Full Width */}
        <div 
          className="mt-4" 
          style={{ 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden', 
            boxSizing: 'border-box', 
            minWidth: 0,
            display: 'flex'
          }}
        >
          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <TrafficQualityChart 
              teamId={teamId}
              appliedFilters={appliedFilters}
              isLoading={false}
            />
          </div>
        </div>

        {/* Campaign Summary Table - Full Width */}
        <div 
          className="mt-4" 
          style={{ 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden', 
            boxSizing: 'border-box', 
            minWidth: 0,
            display: 'flex'
          }}
        >
          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <CampaignSummaryTable 
              teamId={teamId}
              appliedFilters={appliedFilters}
              isLoading={false}
            />
          </div>
        </div>
      </div>

      {/* Filter Popup */}
      <FilterPopup 
        isOpen={isFilterOpen} 
        onClose={() => setIsFilterOpen(false)}
        onFiltersChange={handleFiltersChange}
        appliedFilters={appliedFilters}
        teamId={localStorage.getItem('selectedTeam') || 'Cryptique'}
      />
    </div>
  );
};

export default Source;

